import { ApolloError } from '@apollo/client';
import {
  Box,
  Button,
  HStack,
  IconButton,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { DataTablePageSize, DataTablePanel } from '@gamma/ui/data-table';
import CheckSharpIcon from '@gamma/ui/icons/CheckSharpIcon';
import EditSharpIcon from '@gamma/ui/icons/EditSharpIcon';
import MoreVertSharpIcon from '@gamma/ui/icons/MoreVertSharpIcon';
import { CustomSeverityScoreModal } from '..';
import { AuthContext, OrgTenantsContext } from '@investigator/shared/providers';
import { AlertMetadata, Maybe } from '@investigator/shared/generated/graphql';

import TrackChangesSharpIcon from '@gamma/ui/icons/TrackChangesSharpIcon';
import { Board, Column, Panel } from '@gamma/ui/layout';
import { Alert, ErrorBoundary } from '@gamma/ui/overlay';
import { GraphQLReqStatus } from '@gamma/ui/progress';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useLocation, useOutletContext } from 'react-router';
import { CellProps, Column as ReactTableCol, SortingRule } from 'react-table';
import { i18n } from '@investigator/shared/localization';
import {
  DETECTION_METHOD_SUMMARY_QUERY,
  IQueryAlertMetadataPaginated,
  QUERY_ALERT_METADATA_PAGINATED,
  QueryAlertMetadataPaginatedVariables,
  SetAlertStatusBySearchTermFiltersInput,
  useGetDetectionMethodSummary,
  useGetEntitySuppressionConfiguration,
  useSetAlertActiveStatus,
  useSetAlertStatusBySearchTerm,
} from '../../queries';
import { stringifyAlertType } from '@investigator/shared/utils';

import NotificationsOffOutlinedIcon from '@gamma/ui/icons/NotificationsOffOutlinedIcon';
import { graphqlErrorRedirects, ROUTES } from '@investigator/shared/constants';
import { useUserSession } from '@investigator/shared/hooks';
import { SeverityScore } from '@investigator/shared/components';
import {
  AlertsCatalogSettingsModal,
  GlobalSuppressionSettingsModal,
} from '../../pages';
import {
  AlertCatalogForm,
  AlertCatalogSearch,
  AlertCatalogSearchProps,
  AlertMetaStatusSwitch,
} from './components';
import { AlertTableHeaderStatusMenu } from './components/AlertTableHeaderStatusMenu';
import {
  DetectionMethodSummary,
  DetectionMethodSummaryItem,
} from './components/DetectionMethodSummary';

const { settingPerTenant } = i18n.federatedTenant;

export interface IAlertsCatalogTableContext {
  loading: boolean;
  error?: ApolloError;
  data?: IQueryAlertMetadataPaginated;
  pageCount: number;
  pageIndex: number;
  pageSize: DataTablePageSize;
  sortBy: SortingRule<AlertMetadata>[];
  onFetchData: (
    pageIndex: number,
    pageSize: number,
    sortBy: SortingRule<AlertMetadata>[],
  ) => void;
  hiddenCols: KeyAlertMeta[];
  variables: QueryAlertMetadataPaginatedVariables;
  setQuery: Dispatch<SetStateAction<string | null>>;
  queryTerm: string | null;
  prevQueryTerm: string | null;
  setQueryTerm: Dispatch<SetStateAction<string | null>>;
  setPrevQueryTerm: Dispatch<SetStateAction<string | null>>;
}

type KeyAlertMeta = keyof AlertMetadata;

export const AlertsCatalogTableSearch = ({
  loading,
  queryTerm,
  setQuery,
  setQueryTerm,
  setPrevQueryTerm,
  setAlertStatusBySearchTermFilters,
  onFormDataChange,
}: AlertCatalogSearchProps) =>
  useMemo(
    () => (
      <AlertCatalogSearch
        loading={loading}
        queryTerm={queryTerm}
        setQuery={setQuery}
        setQueryTerm={setQueryTerm}
        setPrevQueryTerm={setPrevQueryTerm}
        setAlertStatusBySearchTermFilters={setAlertStatusBySearchTermFilters}
        onFormDataChange={onFormDataChange}
      />
    ),
    [
      loading,
      queryTerm,
      setQuery,
      setQueryTerm,
      setPrevQueryTerm,
      setAlertStatusBySearchTermFilters,
      onFormDataChange,
    ],
  );

export const AlertsCatalogTable = ({
  _hasSelectedAll,
}: {
  _hasSelectedAll?: boolean;
}) => {
  const {
    data,
    error,
    sortBy,
    loading,
    pageSize,
    pageIndex,
    pageCount,
    variables,
    onFetchData,
    hiddenCols,
    setQuery,
    queryTerm,
    setQueryTerm,
    setPrevQueryTerm,
  } = useOutletContext<IAlertsCatalogTableContext>();

  const { signOut } = useUserSession();
  const { orgTenantsQueryParam } = useContext(OrgTenantsContext);

  const [alertStatusBySearchTermFilters, setAlertStatusBySearchTermFilters] =
    useState<SetAlertStatusBySearchTermFiltersInput>({
      active: [],
      severity: [],
      types: [],
    });

  const { search } = useLocation();
  const params = useMemo(() => new URLSearchParams(search), [search]);

  // Get dates from variables which comes from Outlet context
  const { start, end } = variables;

  const showToastMessage = useToast();

  const severityDisclosure = useDisclosure();

  const { userPrivilegesLoading, isOrgTenant, userRole, userLicense } =
    useContext(AuthContext);
  const { tenant = '' } = userLicense ?? {};
  const [hasSelectedAll, setHasSelectedAll] = useState(false);
  const [selectedRows, setSelectedRows] = useState<AlertMetadata[] | []>([]);
  const [currentFormData, setCurrentFormData] =
    useState<AlertCatalogForm | null>(null);
  const [tableKey, setTableKey] = useState(0); // Force table re-mount

  const { detections } = ROUTES;

  const {
    editAlert,
    viewAllDetections,
    errorRes,
    statusNotUpdated,
    successRes,
    alertStatusActive,
    alertStatusInactive,
    alertsStatusActive,
    alertsStatusInactive,
    selectAllItems,
    globalSuppressionButtonCopy,
    edit,
    add,
    settings,
  } = i18n.pages.system.alertCatalog;

  const [
    setAlertStatusBySearchTerm,
    { loading: setAlertStatusBySearchTermLoading },
  ] = useSetAlertStatusBySearchTerm({
    notifyOnNetworkStatusChange: true,
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: QUERY_ALERT_METADATA_PAGINATED,
        variables: variables,
      },
      {
        query: DETECTION_METHOD_SUMMARY_QUERY,
        variables: { tenant: orgTenantsQueryParam?.[0] },
      },
    ],
  });

  const [setAlertActiveStatus, { loading: bulkLoading }] =
    useSetAlertActiveStatus({
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: QUERY_ALERT_METADATA_PAGINATED,
          variables: variables,
        },
        {
          query: DETECTION_METHOD_SUMMARY_QUERY,
          variables: { tenant: orgTenantsQueryParam?.[0] },
        },
      ],
      notifyOnNetworkStatusChange: true,
      onError: () => {
        showToastMessage({
          status: 'error',
          title: errorRes,
          description: error?.message,
          isClosable: true,
          position: 'bottom-right',
        });
      },
      onCompleted: ({ setAlertActiveStatus: { metadata } }) => {
        setTableKey((prev) => prev + 1); // Force table re-mount
        const setToActive = Boolean(metadata?.[0].active);
        const makePluralAlertMessage =
          metadata?.length > 1 || selectedRows?.length > 1;
        const whichAlertStatusActive = makePluralAlertMessage
          ? alertsStatusActive
          : alertStatusActive;
        const whichAlertStatusInactive = makePluralAlertMessage
          ? alertsStatusInactive
          : alertStatusInactive;
        showToastMessage({
          status: 'success',
          title: successRes,
          description: setToActive
            ? whichAlertStatusActive
            : whichAlertStatusInactive,
          isClosable: true,
          position: 'bottom-right',
        });
      },
    });

  const getSelectedFilters = (formData: AlertCatalogForm) => {
    if (!formData) return { active: [], severity: [], types: [] };
    return {
      active: formData.active
        ? formData.active
            .split(', ')
            .map((value: string) => Boolean(Number(value)))
        : [],
      severity: formData.severity ? formData.severity.split(', ') : [],
      types: formData.category ? formData.category.split(', ') : [],
    };
  };

  const alertStatusUpdate = async (status: boolean) => {
    if (hasSelectedAll || _hasSelectedAll) {
      try {
        const currentFilters = currentFormData
          ? getSelectedFilters(currentFormData)
          : alertStatusBySearchTermFilters;

        await setAlertStatusBySearchTerm({
          variables: {
            items: {
              tenant: orgTenantsQueryParam?.[0],
              active: status,
              search_term: queryTerm ?? '',
              filters: currentFilters,
            },
          },
        }).then(() => {
          setTableKey((prev) => prev + 1); // This forces a table re-mount. internal state of table is otherwise not reset
          showToastMessage({
            status: 'success',
            title: successRes,
            description: status
              ? `${totalItems} ${alertsStatusActive}`
              : `${totalItems} ${alertsStatusInactive}`,
            isClosable: true,
            position: 'bottom-right',
          });
        });
      } catch {
        showToastMessage({
          status: 'error',
          title: errorRes,
          description: statusNotUpdated,
          isClosable: true,
          position: 'bottom-right',
        });
      }
    } else {
      setAlertActiveStatus({
        variables: {
          items: selectedRows.map((row) => {
            return {
              tenant: orgTenantsQueryParam?.[0],
              active: status,
              content_id: row.content_id,
              alert_name: row.title,
              alert_type: row.logsource?.category,
            };
          }),
        },
      });
    }
  };

  const [
    alertForCustomSeverityScoreModal,
    setAlertForCustomSeverityScoreModal,
  ] = useState<AlertMetadata | null>(null);

  const handleViewAllDetections = useCallback(
    (alertTitle: string) => {
      const searchParams = new URLSearchParams();
      searchParams.set('start', String(start));
      searchParams.set('end', String(end));
      searchParams.set('alert_category', alertTitle);
      const url = `${detections}?${searchParams.toString()}`;
      // Use window.location.href for more reliable navigation
      window.location.href = url;
    },
    [start, end, detections],
  );

  const dataTableColumns: ReactTableCol<AlertMetadata>[] = useMemo(
    () => [
      {
        width: 80,
        Header: i18n.tables.status,
        accessor: 'active',
        Cell: (props) =>
          orgTenantsQueryParam?.[0] && (
            <AlertMetaStatusSwitch
              variables={variables}
              tenant={orgTenantsQueryParam?.[0]}
              activeRow={props.cell.row.original}
            />
          ),
        disableSortBy: true,
      },
      {
        width: 115,
        align: 'center',
        Header: i18n.tables.severity,
        accessor: 'severity',
        Cell: (props: CellProps<AlertMetadata, Maybe<number> | undefined>) => {
          const customScore =
            props.cell.row.original?.severity_info?.custom_severity ?? 0;
          const score = Number(props.cell.row.original?.severity);

          return (
            <SeverityScore
              dataTestId={`alert-catalog-table-severity-score-${props?.cell?.row?.index}`}
              score={customScore ? customScore : score}
            />
          );
        },
      },
      {
        Header: i18n.tables.securityAlertCategory,
        accessor: 'title',
        disableSortBy: true,
      },
      {
        width: 150,
        Header: i18n.tables.type,
        accessor: 'logsource',
        Cell: (props) => {
          const { original } = props.cell.row;
          return original.logsource?.category
            ? stringifyAlertType(original.logsource?.category)
            : i18n.pages.system.alertCatalog.none;
        },
        disableSortBy: true,
      },
      {
        width: 184,
        Header: i18n.tables.actions,
        accessor: 'tenant_alert',
        Cell: (props) => {
          const { original, index } = props.cell.row;
          return (
            <HStack>
              {userRole !== 'viewer' && (
                <Tooltip label={editAlert} placement="bottom-end">
                  <IconButton
                    data-testid={`alert-catalog-table-edit-score-button-${index}`}
                    size="box-md"
                    variant="solid"
                    colorScheme="gray"
                    icon={<EditSharpIcon />}
                    aria-label={editAlert}
                    onClick={() => {
                      severityDisclosure?.onOpen();
                      setAlertForCustomSeverityScoreModal(original);
                    }}
                  />
                </Tooltip>
              )}
              <Tooltip label={viewAllDetections} placement="bottom-end">
                <IconButton
                  size="box-md"
                  variant="solid"
                  colorScheme="gray"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewAllDetections(original?.title || '');
                  }}
                  icon={<TrackChangesSharpIcon boxSize={5} />}
                  aria-label={viewAllDetections}
                  data-testid="alert-catalog-alerts-details-button"
                />
              </Tooltip>
            </HStack>
          );
        },
        disableSortBy: true,
      },
      {
        accessor: 'content_id',
      },
      {
        accessor: '_score',
      },
    ],
    [
      editAlert,
      handleViewAllDetections,
      orgTenantsQueryParam,
      severityDisclosure,
      userRole,
      variables,
      viewAllDetections,
    ],
  );

  const filteredDataTableColumns = useMemo(
    () =>
      dataTableColumns.filter(
        (col) => !hiddenCols.includes(col.accessor as KeyAlertMeta) && col,
      ),
    [dataTableColumns, hiddenCols],
  );

  const metadata = data?.queryAlertMetadataPaginated?.metadata || [];
  const totalItems = data?.queryAlertMetadataPaginated?.total_items || 0;

  const { data: methodData, loading: methodLoading } =
    useGetDetectionMethodSummary({
      variables: {
        tenant: (orgTenantsQueryParam?.[0] || tenant) ?? '',
      },
    });

  const { alertCatalog } = i18n.navigation.system;

  const {
    data: globalSuppressionConfiguration,
    loading: globalSuppressionConfigurationLoading,
  } = useGetEntitySuppressionConfiguration({
    variables: {
      tenant: (orgTenantsQueryParam?.[0] || tenant) ?? '',
    },
    skip: userRole !== 'admin',
  });

  const buttonActionCopy = globalSuppressionConfiguration
    ?.getEntitySuppressionConfiguration?.file_content
    ? edit
    : add;

  const {
    isOpen: alertSettingsModalIsOpen,
    onOpen: alertSettingsModalOnOpen,
    onClose: alertSettingsModalOnClose,
  } = useDisclosure();
  const {
    isOpen: globalSuppressionSettingsModalIsOpen,
    onOpen: globalSuppressionSettingsModalOnOpen,
    onClose: globalSuppressionSettingsModalOnClose,
  } = useDisclosure();

  return (
    <ErrorBoundary page="Alert Catalog" styleClass="main">
      <HStack
        width="100%"
        padding={3}
        justifyContent="space-between"
        marginTop={-5}
      >
        <Text textStyle="h3" fontWeight={400}>
          {alertCatalog}
        </Text>
        <Box>
          {userRole === 'admin' && (
            <Button
              variant="solid"
              colorScheme="gray"
              aria-label="Global Suppression Settings Button"
              onClick={globalSuppressionSettingsModalOnOpen}
              data-testid="global-supression-button"
              isDisabled={globalSuppressionConfigurationLoading}
              isLoading={globalSuppressionConfigurationLoading}
            >
              <NotificationsOffOutlinedIcon />
              &nbsp;
              {i18n.formatString(globalSuppressionButtonCopy, buttonActionCopy)}
            </Button>
          )}
          {userRole !== 'viewer' && (
            <Tooltip label={settings}>
              <IconButton
                size="sm"
                variant="ghost"
                color="inherit"
                aria-label="Alert Settings Button"
                onClick={alertSettingsModalOnOpen}
                data-testid="alert-settings-button"
                icon={<MoreVertSharpIcon />}
                marginLeft={1}
              />
            </Tooltip>
          )}
        </Box>
      </HStack>
      <GlobalSuppressionSettingsModal
        key={`global-suppression-settings-${orgTenantsQueryParam?.[0]}`}
        isOpen={globalSuppressionSettingsModalIsOpen}
        onClose={globalSuppressionSettingsModalOnClose}
        selectedTenant={orgTenantsQueryParam?.[0]}
      />
      <AlertsCatalogSettingsModal
        isOpen={alertSettingsModalIsOpen}
        onClose={alertSettingsModalOnClose}
        selectedTenant={orgTenantsQueryParam?.[0]}
      />
      <Board data-testid="alert-catalog-table-board">
        {isOrgTenant && (
          <Column>
            <Alert variant="subtle" title={settingPerTenant} />
          </Column>
        )}
        <Column>
          <DetectionMethodSummary
            data={
              methodData?.getDetectionMethodSummary as DetectionMethodSummaryItem[]
            }
            loading={methodLoading}
          />
        </Column>
        <AlertsCatalogTableSearch
          loading={bulkLoading}
          queryTerm={queryTerm}
          setQuery={setQuery}
          setQueryTerm={setQueryTerm}
          setPrevQueryTerm={setPrevQueryTerm}
          setAlertStatusBySearchTermFilters={setAlertStatusBySearchTermFilters}
          onFormDataChange={setCurrentFormData}
        />
        {loading || userPrivilegesLoading ? (
          <Column>
            <Panel>
              <GraphQLReqStatus
                signOut={signOut}
                extended={graphqlErrorRedirects}
                loading={loading || !!userPrivilegesLoading}
              />
            </Panel>
          </Column>
        ) : (
          <>
            <Column>
              <DataTablePanel
                key={tableKey}
                autoResetRowSelection={false}
                data-testid="alert-catalog-table"
                isSelectable={userRole !== 'viewer'}
                isLoading={bulkLoading || setAlertStatusBySearchTermLoading}
                onRowSelection={(rows) => {
                  setHasSelectedAll(false);
                  setSelectedRows(rows.map((row) => row.original));
                }}
                data={metadata}
                pageSize={pageSize}
                pageCount={pageCount}
                autoResetPage={false}
                autoResetSortBy={false}
                isPaginationManual={true}
                onFetchData={onFetchData}
                columns={filteredDataTableColumns}
                itemCount={totalItems || 0}
                onRowClick={({ original }, event) => {
                  const target = event.target as HTMLElement;
                  const className = target?.className;
                  if (
                    className &&
                    typeof className === 'string' &&
                    !['icon', 'button', 'switch'].some((classNameString) =>
                      className.includes(classNameString),
                    )
                  ) {
                    // Manual navigation workaround for React Router
                    const url = `/system/settings/alert-catalog/${original.content_id}?${params.toString()}`;
                    window.history.pushState({}, '', url);
                    window.dispatchEvent(new PopStateEvent('popstate'));
                  }
                }}
                hasSelectedAllRows={hasSelectedAll}
                onSelectAllClick={() => setHasSelectedAll((prev) => !prev)}
                onCancelClick={() => setHasSelectedAll(false)}
                initialState={{
                  pageIndex,
                  sortBy,
                  hiddenColumns: hiddenCols,
                }}
                renderSelectedActions={() => (
                  <AlertTableHeaderStatusMenu
                    loading={bulkLoading || setAlertStatusBySearchTermLoading}
                    alertStatusUpdate={alertStatusUpdate}
                  />
                )}
                selectAllText={
                  <>
                    <Box
                      mr={2}
                      w="18px"
                      h="18px"
                      bg="transparent"
                      borderRadius="base"
                      border="solid 1px white"
                    >
                      {hasSelectedAll && <CheckSharpIcon size="xs" />}
                    </Box>
                    <Text>
                      {i18n.formatString(selectAllItems, String(totalItems))}
                    </Text>
                  </>
                }
                selectAllProps={{
                  isCustomSelectAll: true,
                  leftIcon: undefined,
                  isDisabled: bulkLoading || setAlertStatusBySearchTermLoading,
                }}
                numberOfSelectAllItems={
                  hasSelectedAll ? totalItems : selectedRows.length
                }
                onPageIndexChange={() => setHasSelectedAll(false)}
                emptyBodyContent={i18n.pages.alerts.na}
              />
            </Column>
            {userRole !== 'viewer' &&
              severityDisclosure?.isOpen &&
              alertForCustomSeverityScoreModal && (
                <CustomSeverityScoreModal
                  isFromAlertCatalogTable={true}
                  severityDisclosure={severityDisclosure}
                  alert={alertForCustomSeverityScoreModal}
                />
              )}
          </>
        )}
      </Board>
    </ErrorBoundary>
  );
};
